import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { DollarSign, Wallet, Receipt, Percent, TrendingUp, TrendingDown, Printer, Download, HardHat, Clock, ArrowDownCircle, ArrowUpCircle } from "lucide-react";

function Stat({ icon: Icon, label, value, accent, sub }) {
  return (
    <div className="rounded-sm border border-slate-200 bg-white p-5" data-testid={`acc-stat-${label}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">{label}</span>
        <Icon className={`h-5 w-5 ${accent || "text-primary"}`} />
      </div>
      <p className={`mt-2 font-head text-2xl font-extrabold ${accent || "text-secondary"}`}>{value}</p>
      {sub && <p className="mt-0.5 text-xs text-slate-400">{sub}</p>}
    </div>
  );
}

export default function Accounting() {
  const { lang } = useLang();
  const today = new Date();
  const [start, setStart] = useState(`${today.getFullYear()}-01-01`);
  const [end, setEnd] = useState(today.toISOString().slice(0, 10));
  const [d, setD] = useState(null);
  const [biz, setBiz] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/accounting", { params: { start, end } });
      setD(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  }, [start, end]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/businesses/mine").then((r) => setBiz(r.data)).catch(() => {}); }, []);

  const exportCsv = () => {
    if (!d) return;
    const lines = [
      ["Concepto", "Monto"],
      [lang === "es" ? "Facturado" : "Invoiced", d.invoiced_total],
      [lang === "es" ? "Cobrado" : "Collected", d.collected_total],
      [lang === "es" ? "Pendiente por cobrar" : "Pending", d.pending_total],
      [lang === "es" ? "Impuestos facturados" : "Taxes collected", d.tax_collected],
      [lang === "es" ? "Comisión plataforma" : "Platform commission", d.commission_paid],
      [lang === "es" ? "Gastos de proyectos" : "Project expenses", d.project_expenses],
      [lang === "es" ? "Nómina pagada" : "Payroll paid", d.payroll_paid],
      [lang === "es" ? "Egresos totales" : "Total expenses", d.total_expenses],
      [lang === "es" ? "Utilidad neta" : "Net profit", d.net_profit],
      [],
      [lang === "es" ? "Mes" : "Month", lang === "es" ? "Ingresos" : "Income", lang === "es" ? "Egresos" : "Expenses", lang === "es" ? "Neto" : "Net"],
      ...d.monthly.map((m) => [m.month, m.income, m.expense, m.net]),
    ];
    const csv = lines.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `contabilidad_${start}_${end}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="accounting-page">
      <div className="no-print flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-head text-2xl font-extrabold tracking-tight text-secondary">{lang === "es" ? "Contabilidad" : "Accounting"}</h1>
          <p className="mt-1 text-sm text-slate-500">{lang === "es" ? "Administra las finanzas de tu negocio: ingresos, egresos, impuestos y utilidad neta." : "Manage your business finances: income, expenses, taxes and net profit."}</p>
        </div>
        <div className="flex items-end gap-2">
          <div><Label className="text-xs font-bold uppercase text-slate-500">{lang === "es" ? "Desde" : "From"}</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1 rounded-sm" data-testid="acc-start" /></div>
          <div><Label className="text-xs font-bold uppercase text-slate-500">{lang === "es" ? "Hasta" : "To"}</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1 rounded-sm" data-testid="acc-end" /></div>
          <Button variant="outline" className="rounded-sm" onClick={exportCsv} data-testid="acc-export-csv"><Download className="mr-2 h-4 w-4" />CSV</Button>
          <Button className="rounded-sm bg-secondary hover:bg-slate-700" onClick={() => window.print()} data-testid="acc-print"><Printer className="mr-2 h-4 w-4" />PDF</Button>
        </div>
      </div>

      {d && (
        <div className="accounting-print mt-6">
          <div className="mb-4 hidden items-center gap-3 border-b border-slate-200 pb-4 print:flex">
            {biz?.logo ? <img src={biz.logo} alt="logo" className="h-12 w-12 object-contain" /> : <span className="flex h-12 w-12 items-center justify-center rounded-sm bg-primary text-white"><HardHat className="h-6 w-6" /></span>}
            <div><p className="font-head text-lg font-bold text-secondary">{biz?.name}</p><p className="text-xs text-slate-400">{lang === "es" ? "Reporte financiero" : "Financial report"} · {d.period.start} → {d.period.end}</p></div>
          </div>

          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Stat icon={Wallet} label={lang === "es" ? "Ingresos (cobrado)" : "Income (collected)"} value={`$${d.collected_total.toFixed(2)}`} accent="text-accent" sub={`${lang === "es" ? "Facturado" : "Invoiced"}: $${d.invoiced_total.toFixed(2)}`} />
            <Stat icon={TrendingDown} label={lang === "es" ? "Egresos totales" : "Total expenses"} value={`$${d.total_expenses.toFixed(2)}`} accent="text-destructive" sub={`${lang === "es" ? "Gastos" : "Exp"}: $${d.project_expenses.toFixed(2)} · ${lang === "es" ? "Nómina" : "Payroll"}: $${d.payroll_paid.toFixed(2)} · ${lang === "es" ? "Cuentas" : "Bills"}: $${d.bills_paid.toFixed(2)}`} />
            <Stat icon={Receipt} label={lang === "es" ? "Impuestos" : "Taxes"} value={`$${d.tax_collected.toFixed(2)}`} sub={`${lang === "es" ? "Comisión" : "Commission"}: $${d.commission_paid.toFixed(2)}`} />
            <Stat icon={TrendingUp} label={lang === "es" ? "Utilidad neta" : "Net profit"} value={`$${d.net_profit.toFixed(2)}`} accent={d.net_profit >= 0 ? "text-accent" : "text-destructive"} />
          </div>

          <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Stat icon={ArrowDownCircle} label={lang === "es" ? "Por cobrar" : "Receivable"} value={`$${d.accounts_receivable.toFixed(2)}`} accent="text-accent" />
            <Stat icon={ArrowUpCircle} label={lang === "es" ? "Por pagar" : "Payable"} value={`$${d.accounts_payable.toFixed(2)}`} accent="text-primary" />
            <Stat icon={DollarSign} label={lang === "es" ? "Cuentas pagadas" : "Bills paid"} value={`$${d.bills_paid.toFixed(2)}`} />
            <Stat icon={Percent} label={lang === "es" ? "Comisión plataforma" : "Commission"} value={`$${d.commission_paid.toFixed(2)}`} accent="text-primary" />
          </div>

          <div className="mt-6 rounded-sm border border-slate-200 bg-white p-6" data-testid="accounting-chart">
            <h2 className="font-head text-lg font-bold text-secondary">{lang === "es" ? "Ingresos vs Egresos por mes" : "Income vs Expenses by month"}</h2>
            {d.monthly.length === 0 ? (
              <p className="py-10 text-center text-slate-400">{lang === "es" ? "Sin movimientos en este periodo." : "No activity in this period."}</p>
            ) : (
              <div className="mt-4 h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={d.monthly} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => `$${v}`} />
                    <Tooltip formatter={(v) => `$${Number(v).toFixed(2)}`} cursor={{ fill: "rgba(148,163,184,0.1)" }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="income" name={lang === "es" ? "Ingresos" : "Income"} fill="#10B981" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="expense" name={lang === "es" ? "Egresos" : "Expenses"} fill="#EF4444" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
